import React, { useCallback, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW } from 'lexical';
import { $createContactMentionNode } from '../nodes/ContactMentionNode';
import { $createMatterReferenceNode } from '../nodes/MatterReferenceNode';
import { $createBillReferenceNode } from '../nodes/BillReferenceNode';
import { $createDocumentReferenceNode } from '../nodes/DocumentReferenceNode';
import { useKeycloak } from '@react-keycloak/web';

export interface MentionItem {
  id: string;
  display: string;
  data?: any;
}

interface MentionPluginProps {
  onMentionSelect?: (mention: MentionItem, type: string) => void;
}

/**
 * API function to fetch contacts
 */
const fetchContacts = async (token: string): Promise<MentionItem[]> => {
  try {
    const response = await fetch('/api/v1/clients', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.warn('Failed to fetch contacts for mentions');
      return [];
    }
    
    const data = await response.json();
    const contacts = Array.isArray(data) ? data : data.data || data.clients || [];
    
    return contacts.map((contact: any) => ({
      id: contact.id,
      display: `${contact.first_name || contact.firstName || ''} ${contact.last_name || contact.lastName || ''}`.trim() || contact.name || 'Unnamed Contact',
      data: contact
    }));
  } catch (error) {
    console.warn('Error fetching contacts for mentions:', error);
    return [];
  }
};

/**
 * API function to fetch matters
 */
const fetchMatters = async (token: string): Promise<MentionItem[]> => {
  try {
    const response = await fetch('/api/v1/matters', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.warn('Failed to fetch matters for mentions');
      return [];
    }
    
    const data = await response.json();
    const matters = Array.isArray(data) ? data : data.data || data.matters || [];
    
    return matters.map((matter: any) => ({
      id: matter.id,
      display: matter.title || matter.name || matter.case_name || 'Unnamed Matter',
      data: matter
    }));
  } catch (error) {
    console.warn('Error fetching matters for mentions:', error);
    return [];
  }
};

/**
 * For now, we'll return empty arrays for bills and documents since those APIs aren't implemented yet
 * These can be updated when those features are added
 */
const fetchBills = async (_token: string): Promise<MentionItem[]> => {
  // TODO: Implement when billing API is available
  console.log('Bills API not yet implemented for mentions');
  return [];
};

const fetchDocuments = async (_token: string): Promise<MentionItem[]> => {
  // TODO: Implement when DMS API is available for mentions
  console.log('Documents API not yet implemented for mentions');
  return [];
};

/**
 * Hook to get JWT token (same pattern as calendar API)
 */
const useAuthToken = () => {
  const { keycloak } = useKeycloak();
  
  return useCallback(() => {
    if (keycloak?.token) {
      return keycloak.token;
    }
    
    // Fallback to storage-based detection
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) continue;
        
        if (jwtPattern.test(value)) {
          return value;
        }
        
        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
            for (const field of tokenFields) {
              if (parsed[field] && jwtPattern.test(parsed[field])) {
                return parsed[field];
              }
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
    
    return '';
  }, [keycloak]);
};

/**
 * Enhanced search function that fetches real data
 */
const searchMentions = async (type: string, query: string, token: string): Promise<MentionItem[]> => {
  let items: MentionItem[] = [];
  
  try {
    switch (type) {
      case 'contact':
        items = await fetchContacts(token);
        break;
      case 'matter':
        items = await fetchMatters(token);
        break;
      case 'bill':
        items = await fetchBills(token);
        break;
      case 'document':
        items = await fetchDocuments(token);
        break;
      default:
        return [];
    }
    
    // Filter based on query
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      items = items.filter(item => 
        item.display.toLowerCase().includes(lowerQuery)
      );
    }
    
    // Limit results to prevent overwhelming UI
    return items.slice(0, 10);
  } catch (error) {
    console.warn(`Error searching ${type} mentions:`, error);
    return [];
  }
};

export const MentionPlugin: React.FC<MentionPluginProps> = ({ onMentionSelect }) => {
  const [editor] = useLexicalComposerContext();
  const getToken = useAuthToken();

  // Handle mention insertion
  const handleMentionSelect = useCallback((item: MentionItem, type: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        let mentionNode;
        
        switch (type) {
          case 'contact':
            mentionNode = $createContactMentionNode({
              contactId: item.id,
              displayName: item.display,
              contactType: 'client',
            });
            break;
          case 'matter':
            mentionNode = $createMatterReferenceNode({
              matterId: item.id,
              displayName: item.display,
              matterType: 'other',
              status: 'active',
            });
            break;
          case 'bill':
            mentionNode = $createBillReferenceNode({
              billId: item.id,
              displayName: item.display,
              amount: 0,
              status: 'draft',
            });
            break;
          case 'document':
            mentionNode = $createDocumentReferenceNode({
              documentId: item.id,
              displayName: item.display,
              documentType: 'other',
              fileType: 'other',
            });
            break;
          default:
            return;
        }
        
        if (mentionNode) {
          selection.insertNodes([mentionNode]);
        }
      }
    });
    
    onMentionSelect?.(item, type);
  }, [editor, onMentionSelect]);

  // Enhanced search with real API data
  const searchItems = useCallback(async (type: string, query: string): Promise<MentionItem[]> => {
    const token = getToken();
    if (!token) {
      console.warn('No auth token available for mention search');
      return [];
    }
    
    return searchMentions(type, query, token);
  }, [getToken]);

  // Register mention triggers
  useEffect(() => {
    const triggers = [
      { symbol: '@', type: 'contact' },
      { symbol: '#', type: 'matter' },
      { symbol: '$', type: 'bill' },
      { symbol: '/doc', type: 'document' }
    ];

    const unregisterList: (() => void)[] = [];

    triggers.forEach(({ symbol, type }) => {
      const unregister = editor.registerCommand(
        // Custom command for mentions - would need to be properly defined
        'MENTION_COMMAND' as any,
        () => {
          // This is a simplified implementation
          // In a real implementation, you'd need proper mention handling
          console.log(`Mention trigger: ${symbol} for ${type}`);
          return false;
        },
        COMMAND_PRIORITY_LOW
      );
      
      unregisterList.push(unregister);
    });

    return () => {
      unregisterList.forEach(fn => fn());
    };
  }, [editor, handleMentionSelect, searchItems]);

  // This plugin doesn't render anything visible - it just handles the mention logic
  return null;
}; 